import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Camera, CheckCircle2, ArrowRight, Loader2, Gift, Crown, Wand2 } from "lucide-react";
import { getUserId } from "../lib/api";
import BeforeAfter from "../components/BeforeAfter";
import { debugLog } from "../lib/logger";
import { T } from "@/locales/tr";
import { PaymentNotice } from "@/components/PaymentNotice";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PKG_ICON = { decouverte: Camera, studio: Wand2, presidentiel: Crown };

const STYLES = [
  { id: "naturel", label: "Naturel · peau parfaite" },
  { id: "mode", label: T("Mode · éditorial") },
  { id: "presidentiel", label: T("Présidentiel") },
  { id: "fashion", label: "Fashion editorial" },
  { id: "premium", label: T("Peau premium · velouté") },
  { id: "food", label: "Food & lifestyle" },
  { id: "evenement", label: T("Mariage & événement") },
];

const fmtEur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;

export default function Retouche() {
  const [pkgs, setPkgs] = useState([]);
  const [showcase, setShowcase] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTrial, setShowTrial] = useState(false);
  const [trial, setTrial] = useState({ name: "", email: "", phone: "", style: "naturel", note: "" });
  const [openBuy, setOpenBuy] = useState(null);
  const [buyer, setBuyer] = useState({ buyer_name: "", buyer_email: "", buyer_phone: "", style: "naturel", note: "" });

  useEffect(() => {
    axios.get(`${API}/retouch/packages`).then((r) => setPkgs(r.data)).catch((e) => debugLog("retouch pkgs:", e?.message));
    axios.get(`${API}/retouch/showcase`).then((r) => setShowcase(r.data)).catch((e) => debugLog("showcase:", e?.message));
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("session_id");
    if (sid) window.location.href = `/retouche/success?session_id=${sid}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submitTrial = async () => {
    if (!trial.name.trim() || !trial.email.trim()) { toast.error(T("Prénom et email requis.")); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/retouch/trial`, { ...trial, user_id: getUserId() });
      toast.success(T("Salaam — Papicoo te répond sous 48 h 🌹"));
      setShowTrial(false);
      setTrial({ name: "", email: "", phone: "", style: "naturel", note: "" });
    } catch { toast.error(T("Échec — réessaie.")); }
    finally { setLoading(false); }
  };

  const buy = async (packageId) => {
    if (!buyer.buyer_name.trim() || !buyer.buyer_email.trim()) { toast.error(T("Nom et email requis.")); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/retouch/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        user_id: getUserId(),
        ...buyer,
      });
      if (data?.url) window.location.href = data.url;
    } catch (e) { toast.error(e?.response?.data?.detail || T("Échec du checkout.")); setLoading(false); }
  };

  return (
    <div data-testid="page-retouche" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Retouche photo IA · New Generation")}</div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {T("Le meilleur retoucheur IA")} <br />
        <span className="italic font-light">{T("de Paris à Dakar.")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-4 max-w-3xl leading-relaxed">
        {T("Tous les styles, tous les thèmes —")} <strong>{T("portrait pro, mode éditoriale, présidentiel, peau premium, mariage, food, intérieur")}</strong>{T(". Une signature jamais vue en photographie. Tu envoies, Papicoo retouche, tu publies.")}
      </p>

      {/* Bandeau essai gratuit */}
      <div className="mt-7 papicoo-card p-5 bg-gradient-to-br from-[color:var(--papicoo-sand-deep)] to-white flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="shrink-0 w-12 h-12 rounded-full bg-[color:var(--papicoo-gold)] text-[color:var(--papicoo-ink)] flex items-center justify-center">
          <Gift size={22} />
        </div>
        <div className="flex-1">
          <div className="font-display text-2xl text-[color:var(--papicoo-ink)] leading-tight">{T("Essai gratuit — 1 photo, 0 €")}</div>
          <p className="text-sm text-[color:var(--papicoo-muted)] mt-1">{T("Envoie une photo, Papicoo te livre la version retouchée sous 48 h. Aucun engagement.")}</p>
        </div>
        <button
          data-testid="trial-cta"
          onClick={() => setShowTrial((s) => !s)}
          className="px-5 py-2.5 rounded-full papicoo-btn-primary text-sm inline-flex items-center gap-2 shrink-0"
        >
          <Sparkles size={14} /> {T("Je veux essayer")}
        </button>
      </div>

      {showTrial && (
        <div data-testid="trial-form" className="mt-3 papicoo-card p-5 grid sm:grid-cols-2 gap-3">
          <input data-testid="trial-name" placeholder={T("Ton prénom")} value={trial.name} onChange={(e) => setTrial({ ...trial, name: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
          <input data-testid="trial-email" type="email" placeholder={T("Ton email")} value={trial.email} onChange={(e) => setTrial({ ...trial, email: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
          <input data-testid="trial-phone" placeholder={T("WhatsApp (optionnel)")} value={trial.phone} onChange={(e) => setTrial({ ...trial, phone: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
          <select data-testid="trial-style" value={trial.style} onChange={(e) => setTrial({ ...trial, style: e.target.value })} className="bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm">
            {STYLES.map((s) => <option key={s.id} value={s.id}>{T(s.label)}</option>)}
          </select>
          <textarea data-testid="trial-note" rows={2} placeholder={T("Brève description (visage, contexte, lumière…)")} value={trial.note} onChange={(e) => setTrial({ ...trial, note: e.target.value })} className="sm:col-span-2 bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm" />
          <button data-testid="trial-submit" onClick={submitTrial} disabled={loading} className="sm:col-span-2 px-5 py-3 rounded-full papicoo-btn-gold text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />} Réserver mon essai gratuit
          </button>
        </div>
      )}

      {/* Showcase Avant / Après */}
      {showcase.length > 0 && (
        <div data-testid="showcase" className="mt-10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)]">{T("Preuve sur image")}</div>
          <h2 className="font-display text-2xl sm:text-3xl text-[color:var(--papicoo-ink)] mt-1 mb-5">{T("Glisse — vois la différence.")}</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {showcase.slice(0, 3).map((s) => (
              <BeforeAfter
                key={s.id}
                beforeSrc={s.before_url}
                afterSrc={s.after_url}
                title={T(s.title)}
                label={s.style}
              />
            ))}
          </div>
        </div>
      )}

      {/* 3 packages */}
      <div className="mt-10 grid lg:grid-cols-3 gap-5">
        {pkgs.map((p, idx) => {
          const Icon = PKG_ICON[p.id] || Camera;
          const featured = p.id === "studio";
          return (
            <div key={p.id} data-testid={`pkg-${p.id}`} className={`papicoo-card p-6 flex flex-col ${featured ? "ring-2 ring-[color:var(--papicoo-gold)] relative" : ""}`}>
              {featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-[color:var(--papicoo-gold)] text-[color:var(--papicoo-ink)] text-[10px] uppercase tracking-wider font-medium">
                  {T("Le plus choisi")}
                </div>
              )}
              <Icon size={26} className="text-[color:var(--papicoo-gold)]" />
              <div className="mt-3 font-display text-2xl text-[color:var(--papicoo-ink)]">{T(p.label)}</div>
              <div className="text-sm text-[color:var(--papicoo-muted)] italic mt-1">{T(p.tagline)}</div>
              <div className="font-display text-4xl text-[color:var(--papicoo-ink)] mt-5">{fmtEur(p.price_eur)}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)] mt-1">{p.photos} photo{p.photos > 1 ? "s" : ""} · livré en {p.delay}</div>
              <ul className="mt-5 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[color:var(--papicoo-ink)]">
                    <CheckCircle2 size={14} className="text-[color:var(--papicoo-gold)] shrink-0 mt-0.5" />
                    <span>{T(f)}</span>
                  </li>
                ))}
              </ul>
              <button
                data-testid={`buy-${p.id}`}
                onClick={() => setOpenBuy(openBuy === p.id ? null : p.id)}
                className={`mt-5 px-5 py-3 rounded-full text-sm inline-flex items-center justify-center gap-2 ${featured ? "papicoo-btn-primary" : "bg-white border border-[color:var(--papicoo-border)] hover:bg-[color:var(--papicoo-sand-deep)]/60 text-[color:var(--papicoo-ink)]"}`}
              >
                Choisir {T(p.label)} <ArrowRight size={14} />
              </button>

              {openBuy === p.id && (
                <div data-testid={`buy-form-${p.id}`} className="mt-4 space-y-2 border-t border-[color:var(--papicoo-border)] pt-4">
                  <input data-testid={`buyer-name-${p.id}`} placeholder={T("Ton nom complet")} value={buyer.buyer_name} onChange={(e) => setBuyer({ ...buyer, buyer_name: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                  <input data-testid={`buyer-email-${p.id}`} type="email" placeholder={T("Email pour livraison")} value={buyer.buyer_email} onChange={(e) => setBuyer({ ...buyer, buyer_email: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                  <select value={buyer.style} onChange={(e) => setBuyer({ ...buyer, style: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm">
                    {STYLES.map((s) => <option key={s.id} value={s.id}>{T(s.label)}</option>)}
                  </select>
                  <textarea rows={2} placeholder={T("Note (optionnel)")} value={buyer.note} onChange={(e) => setBuyer({ ...buyer, note: e.target.value })} className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2 text-sm" />
                  <button
                    data-testid={`confirm-buy-${p.id}`}
                    onClick={() => buy(p.id)}
                    disabled={loading}
                    className="w-full px-4 py-2.5 rounded-full papicoo-btn-primary text-sm disabled:opacity-50"
                  >
                    {loading ? "…" : `Payer ${fmtEur(p.price_eur)} — Stripe sécurisé`}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-10 flex justify-center text-center">
        <PaymentNotice />
      </div>
    </div>
  );
}
